import { useState, useTransition } from "react";
import { toast } from "sonner";
import { Icone } from "@/components/Icone";
import { Aviso, Botao, Cartao } from "@/components/casca/formulario";
import type { ProvedorId } from "@/lib/ports/whatsapp";
import type { EstadoConexao } from "@/lib/telas/conexao";
import { removerConexaoAction, salvarConexaoAction } from "@/web/src/acoes-config";

const ENTRADA =
  "w-full rounded-[10px] border border-[var(--color-borda)] bg-[var(--color-painel)] px-3 py-2 text-sm text-[var(--color-tinta)] outline-none transition-colors placeholder:text-[var(--color-fraco)] focus:border-[var(--color-marca)] focus:bg-[var(--color-cartao)]";

/**
 * A conexão de WhatsApp. Cada provedor declara os próprios campos (catálogo);
 * a tela só desenha. Segredo salvo nunca volta: aparece como "preenchido".
 */
export function FormConexao({ estado }: { estado: EstadoConexao }) {
  const [escolhido, setEscolhido] = useState<ProvedorId>(estado.provedor && estado.provedor !== "sandbox" ? estado.provedor : "cloud");
  const [valores, setValores] = useState<Record<string, string>>({});
  const [teste, setTeste] = useState(estado.ultimoTeste);
  const [copiado, setCopiado] = useState(false);
  const [pendente, iniciar] = useTransition();

  const prov = estado.provedores.find((p) => p.id === escolhido)!;
  const mesmo = estado.conectado && estado.provedor === escolhido;
  const url = estado.webhooks[escolhido];

  function enviar(apenasTestar: boolean) {
    iniciar(async () => {
      const r = await salvarConexaoAction(escolhido, valores, apenasTestar);
      if (!r.ok) {
        toast.error(r.erro);
        return;
      }
      setTeste(r.teste ? { ...r.teste, quandoISO: new Date().toISOString() } : null);
      if (apenasTestar) toast[r.teste?.ok ? "success" : "warning"](r.teste?.detalhe ?? "Testado.");
      else {
        toast[r.teste?.ok ? "success" : "warning"](r.teste?.ok ? "Conectado e testado." : `Salvo, mas o teste falhou: ${r.teste?.detalhe}`);
        setValores({});
      }
    });
  }

  return (
    <div className="space-y-6">
      {estado.ilegivel && (
        <Aviso tom="alerta" titulo="A conexão salva não pôde ser lida.">
          A chave de criptografia mudou? Configure de novo abaixo.
        </Aviso>
      )}

      <div>
        <p className="mb-3 text-[13.5px] leading-relaxed text-[var(--color-suave)]">
          Degrau 1 (número de anúncio, descartável): Z-API ou Evolution. Degrau 2 (número da clínica,
          patrimônio): só a oficial da Meta.
        </p>
        <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {estado.provedores.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => {
                setEscolhido(p.id);
                setValores({});
              }}
              className={
                "rounded-xl p-3.5 text-left transition-all " +
                (escolhido === p.id
                  ? "bg-[var(--color-realce)] shadow-[inset_0_0_0_1.5px_var(--color-marca)]"
                  : "bg-[var(--color-painel)] shadow-[inset_0_0_0_1px_var(--color-borda)] hover:bg-[var(--color-cartao)]")
              }
            >
              <p className="flex items-center justify-between gap-2 text-sm font-bold text-[var(--color-tinta)]">
                {p.nome}
                {estado.provedor === p.id && estado.conectado && (
                  <span className="rounded-full bg-[var(--color-realce)] px-2.5 py-0.5 text-xs font-semibold text-[var(--color-marca-forte)]">em uso</span>
                )}
              </p>
              <p className="mt-1 text-[13px] text-[var(--color-suave)]">{p.resumo}</p>
            </button>
          ))}
        </div>

        <div className="space-y-4">
          {prov.ressalva && <Aviso tom="atencao">{prov.ressalva}</Aviso>}

          <Cartao className="space-y-3.5">
            {prov.campos.map((c) => (
              <label key={c.chave} className="block">
                <span className="mb-1.5 flex items-center gap-2 text-[13px] font-semibold text-[var(--color-suave)]">
                  {c.rotulo}
                  {mesmo && estado.preenchidos[c.chave] && (
                    <span className="rounded-full bg-[var(--color-realce)] px-2 py-0.5 text-[11px] font-semibold text-[var(--color-marca-forte)]">preenchido</span>
                  )}
                </span>
                <input
                  type={c.secreto ? "password" : "text"}
                  autoComplete="off"
                  value={valores[c.chave] ?? ""}
                  onChange={(e) => setValores({ ...valores, [c.chave]: e.target.value })}
                  placeholder={mesmo && estado.preenchidos[c.chave] ? "deixe em branco para manter" : (c.exemplo ?? "")}
                  className={ENTRADA}
                />
                {c.ajuda && <span className="mt-1 block text-xs leading-relaxed text-[var(--color-fraco)]">{c.ajuda}</span>}
              </label>
            ))}

            {teste && <Aviso tom={teste.ok ? "bom" : "alerta"}>{teste.detalhe}</Aviso>}

            <div className="flex flex-wrap gap-2 pt-1">
              <Botao variante="secundario" disabled={pendente} onClick={() => enviar(true)}>
                {pendente ? "…" : "Só testar"}
              </Botao>
              <Botao icone="conexao" disabled={pendente} onClick={() => enviar(false)}>
                {pendente ? "…" : "Salvar e testar"}
              </Botao>
              {estado.conectado && (
                <Botao
                  variante="perigo"
                  icone="desconectar"
                  disabled={pendente}
                  onClick={() => {
                    if (!confirm("Desconectar o WhatsApp? A clínica volta ao modo demonstração: o agente responde na bancada, mas não envia para ninguém.")) return;
                    iniciar(async () => {
                      const r = await removerConexaoAction();
                      if (!r.ok) toast.error(r.erro);
                      else {
                        toast.success("Desconectado.");
                        setTeste(null);
                      }
                    });
                  }}
                >
                  Desconectar
                </Botao>
              )}
            </div>
          </Cartao>

          <Cartao>
            <p className="rotulo mb-1.5">Webhook</p>
            <p className="mb-2 text-[13.5px] text-[var(--color-suave)]">Cole esta URL no painel do provedor. É por ela que as mensagens chegam.</p>
            <div className="flex gap-2">
              <input readOnly value={url} className={`${ENTRADA} font-mono text-xs`} />
              <Botao
                variante="secundario"
                icone={copiado ? "check" : "copiar"}
                className="shrink-0"
                onClick={() => {
                  void navigator.clipboard.writeText(url);
                  setCopiado(true);
                  setTimeout(() => setCopiado(false), 1500);
                }}
              >
                {copiado ? "Copiado" : "Copiar"}
              </Botao>
            </div>
            {url.includes("localhost") && (
              <p className="mt-2 text-xs leading-relaxed text-[var(--color-fraco)]">
                Este endereço é local — o provedor não alcança. Em desenvolvimento, exponha com{" "}
                <code className="rounded bg-[var(--color-painel)] px-1">cloudflared tunnel --url http://localhost:3000</code> e use a URL do túnel.
              </p>
            )}
          </Cartao>
        </div>
      </div>
    </div>
  );
}
